/**
 * Account Settings Page Component
 * 
 * Main page for managing user account preferences
 * Includes currency, date/time formats, theme, pay day, dashboard customization, and data retention
 * 
 * Features:
 * - Explicit save with button state changes (grey → blue)
 * - Navigation guard for unsaved changes
 * - Sections: General Settings, Pay Day, Dashboard Customization, Data Retention
 * - WCAG 2.2 AA compliant
 * 
 * @see specs/006-create-the-account/spec.md
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  Save,
  CheckCircle,
  AlertCircle,
  Loader2,
  DollarSign,
  Calendar,
  Palette,
  LayoutDashboard,
  Archive,
  ChevronDown,
  Sun,
  Moon,
  Monitor,
  BarChart3,
  List,
  TrendingUp,
  Settings,
  Wallet
} from 'lucide-react';
import { usePreferences, useUpdatePreferences } from '../../hooks/usePreferences';
import { useTheme } from '../../contexts/ThemeContext';
import { setNavigationGuardState } from './useNavigationGuardState';
import { PageHeader } from '../../components/PageHeader';
import { supabase } from '../../lib/supabase';
import { getCurrentUserId } from '../../lib/supabase';
import { validatePreferencesForm } from '../../utils/preference-validation';
import type {
  UpdatePreferencesInput,
  DashboardPreferences,
  PayFrequency
} from '../../types/preference.types';
import { BusinessReportingSettingsSection } from './BusinessReportingSettingsSection';

/** ISO weekday labels: 1 = Monday … 7 = Sunday */
const WEEKDAY_OPTIONS = [
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
  { value: 7, label: 'Sunday' },
] as const;

export function AccountSettingsPage(): React.ReactElement {
  const navigate = useNavigate();
  const location = useLocation();
  const { data: preferences, isLoading, error: fetchError } = usePreferences();
  const updatePreferencesMutation = useUpdatePreferences();
  const { theme: currentTheme, setTheme } = useTheme();

  // Form state
  const [formData, setFormData] = useState<UpdatePreferencesInput>({});
  const [isDirty, setIsDirty] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [showNavigationDialog, setShowNavigationDialog] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(null);

  // Initialize form data when preferences load
  useEffect(() => {
    if (preferences) {
      setFormData({
        currency: preferences.currency,
        currencyDisplay: preferences.currencyDisplay,
        dateFormat: preferences.dateFormat,
        timeFormat: preferences.timeFormat,
        financialYearStart: preferences.financialYearStart,
        theme: preferences.theme,
        dashboardPreferences: { ...preferences.dashboardPreferences },
        transactionRetentionYears: preferences.transactionRetentionYears,
        payFrequency: preferences.payFrequency,
        payDay: preferences.payDay,
        payAnchorDate: preferences.payAnchorDate,
        estimatedNetPay: preferences.estimatedNetPay,
        payTransactionName: preferences.payTransactionName,
        gstRegistered: preferences.gstRegistered,
        businessName: preferences.businessName,
        businessAbn: preferences.businessAbn,
      });
      setIsDirty(false);
      setHasUnsavedChanges(false);
    }
  }, [preferences]);

  // Sync formData.theme when theme changes via header toggle
  useEffect(() => {
    if (preferences && currentTheme) {
      // Map ThemeContext theme ('system') to preference theme ('auto')
      const prefTheme = currentTheme === 'system' ? 'auto' : currentTheme;
      
      // Only update if different to avoid unnecessary re-renders
      if (preferences.theme !== prefTheme) {
        setFormData(prev => ({
          ...prev,
          theme: prefTheme as 'light' | 'dark' | 'auto',
        }));
      }
    }
  }, [currentTheme, preferences]);

  // Track dirty state (memoized to avoid unnecessary recalculations)
  const dirtyState = useMemo(() => {
    if (!preferences) return false;

    return (
      formData.currency !== preferences.currency ||
      formData.currencyDisplay !== preferences.currencyDisplay ||
      formData.dateFormat !== preferences.dateFormat ||
      formData.timeFormat !== preferences.timeFormat ||
      formData.financialYearStart !== preferences.financialYearStart ||
      formData.theme !== preferences.theme ||
      formData.transactionRetentionYears !== preferences.transactionRetentionYears ||
      formData.payFrequency !== preferences.payFrequency ||
      formData.payDay !== preferences.payDay ||
      formData.payAnchorDate !== preferences.payAnchorDate ||
      formData.estimatedNetPay !== preferences.estimatedNetPay ||
      formData.payTransactionName !== preferences.payTransactionName ||
      formData.gstRegistered !== preferences.gstRegistered ||
      formData.businessName !== preferences.businessName ||
      formData.businessAbn !== preferences.businessAbn ||
      JSON.stringify(formData.dashboardPreferences) !== JSON.stringify(preferences.dashboardPreferences)
    );
  }, [formData, preferences]);

  // Update dirty state when computed value changes
  useEffect(() => {
    setIsDirty(dirtyState);
    setHasUnsavedChanges(dirtyState);
  }, [dirtyState]);

  // Navigation guard for unsaved changes - intercept React Router navigation
  // Using location change detection since useBlocker requires data router
  const previousLocationRef = useRef(location.pathname);
  const isNavigatingRef = useRef(false);

  useEffect(() => {
    // Only intercept if we're not already handling navigation
    if (isNavigatingRef.current) {
      isNavigatingRef.current = false;
      return;
    }

    // Check if location changed and we have unsaved changes
    if (previousLocationRef.current !== location.pathname && hasUnsavedChanges) {
      // Revert navigation by going back
      window.history.pushState(null, '', previousLocationRef.current);
      setPendingNavigation(location.pathname);
      setShowNavigationDialog(true);
    }

    previousLocationRef.current = location.pathname;
  }, [location.pathname, hasUnsavedChanges]);

  // Intercept browser back/forward buttons
  useEffect(() => {
    const handlePopState = () => {
      if (hasUnsavedChanges) {
        // Prevent navigation
        window.history.pushState(null, '', location.pathname);
        setPendingNavigation(previousLocationRef.current);
        setShowNavigationDialog(true);
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [hasUnsavedChanges, location.pathname]);

  // Navigation guard for browser navigation (beforeunload)
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  // Handle field changes
  const handleFieldChange = useCallback((field: keyof UpdatePreferencesInput) => {
    return (value: any) => {
      setFormData(prev => ({
        ...prev,
        [field]: value,
      }));
      
      // Immediately sync theme changes with ThemeContext
      if (field === 'theme') {
        // Map 'auto' to 'system' for ThemeContext
        const themeValue = value === 'auto' ? 'system' : value;
        setTheme(themeValue as 'light' | 'dark' | 'system');
      }
    };
  }, [setTheme]);

  // Handle dashboard preference toggle
  const handleDashboardToggle = useCallback((key: keyof DashboardPreferences) => {
    setFormData(prev => ({
      ...prev,
      dashboardPreferences: {
        ...prev.dashboardPreferences,
        [key]: !prev.dashboardPreferences?.[key],
      } as DashboardPreferences,
    }));
  }, []);

  // Pay frequency change — clear dependent fields when unset or leaving fortnightly
  const handlePayFrequencyChange = useCallback((value: string) => {
    const frequency = (value === '' ? null : value) as PayFrequency | null;
    setFormData(prev => ({
      ...prev,
      payFrequency: frequency,
      payDay: frequency === null ? null : prev.payDay,
      payAnchorDate: frequency === 'fortnightly' ? prev.payAnchorDate : null,
    }));
  }, []);

  // Form validation (memoized to avoid recreation on every render)
  const validateForm = useCallback((): boolean => {
    const { isValid, errors } = validatePreferencesForm(formData);
    setValidationErrors(errors);
    return isValid;
  }, [formData]);

  // Save changes handler (memoized to avoid recreation)
  // Returns true if save was successful, false otherwise
  const handleSave = useCallback(async (): Promise<boolean> => {
    if (!isDirty) return true;

    // Validate form before saving
    if (!validateForm()) {
      setSaveStatus('error');
      setErrorMessage('Please fix validation errors before saving');
      return false;
    }

    setSaveStatus('saving');
    setErrorMessage('');
    setValidationErrors({});

    try {
      // Log what we're trying to save for debugging
      console.log('Saving preferences with dateFormat:', formData.dateFormat);
      
      // Check if retention preference changed
      const retentionChanged = preferences && 
        formData.transactionRetentionYears !== undefined &&
        formData.transactionRetentionYears !== preferences.transactionRetentionYears;
      
      // Save preferences
      await updatePreferencesMutation.mutateAsync(formData);
      
      // If retention preference changed, trigger immediate archiving
      if (retentionChanged) {
        try {
          const userId = await getCurrentUserId();
          if (userId) {
            console.log('[Archive] Retention preference changed, triggering immediate sync for user:', userId);
            
            // Call the PostgreSQL function to sync transactions for this user
            // This handles both archiving (when retention decreases) and unarchiving (when retention increases)
            // Type assertion needed because RPC types aren't fully defined in Database type
            const { data, error } = await supabase.rpc('archive_user_transactions', {
              p_user_id: userId
            } as any) as { data: Array<{ archived_count: number; unarchived_count: number; skipped: boolean; error_message: string | null }> | null; error: any };
            
            if (error) {
              console.error('[Archive] Failed to sync transactions:', error);
              // Don't fail the save - syncing will happen on next scheduled run
            } else if (data && data.length > 0) {
              const result = data[0];
              if (result.archived_count > 0) {
                console.log(`[Archive] Successfully archived ${result.archived_count} transactions`);
              }
              if (result.unarchived_count > 0) {
                console.log(`[Archive] Successfully unarchived ${result.unarchived_count} transactions`);
              }
              if (result.skipped) {
                console.log('[Archive] Sync skipped (Keep Forever retention - all transactions unarchived)');
              }
            }
          }
        } catch (archiveError: any) {
          // Log but don't fail the save - syncing will happen on next scheduled run
          console.error('[Archive] Error during immediate sync:', archiveError);
        }
      }
      
      setSaveStatus('saved');
      setIsDirty(false);
      setHasUnsavedChanges(false);
      setTimeout(() => setSaveStatus('idle'), 3000);
      return true;
    } catch (error: any) {
      console.error('Error saving preferences:', error);
      console.error('Form data dateFormat:', formData.dateFormat);
      setErrorMessage(error.message || 'Failed to save preferences');
      setSaveStatus('error');
      setTimeout(() => {
        setSaveStatus('idle');
        setErrorMessage('');
      }, 5000);
      return false;
    }
  }, [isDirty, validateForm, formData, preferences, updatePreferencesMutation, setTheme]);

  // Navigation guard handlers (memoized for stable references)
  const handleNavigationAttempt = useCallback((path: string) => {
    if (hasUnsavedChanges) {
      setPendingNavigation(path);
      setShowNavigationDialog(true);
    } else {
      navigate(path);
    }
  }, [hasUnsavedChanges, navigate]);

  // Register guard state with SettingsLayout
  useEffect(() => {
    setNavigationGuardState(hasUnsavedChanges, handleNavigationAttempt);
    return () => {
      // Clear guard state when component unmounts
      setNavigationGuardState(false, null);
    };
  }, [hasUnsavedChanges, handleNavigationAttempt]);

  const handleNavigationDialogSave = useCallback(async () => {
    if (pendingNavigation) {
      const saveSuccessful = await handleSave();
      if (saveSuccessful) {
        // Close dialog
        setShowNavigationDialog(false);
        // Clear pending navigation (user stays on current page)
        setPendingNavigation(null);
        // Saved message will be displayed by handleSave function
      }
      // If save failed, dialog stays open and error message is already displayed
    }
  }, [pendingNavigation, handleSave]);

  const handleNavigationDialogDiscard = useCallback(() => {
    setShowNavigationDialog(false);
    // Reset form data to match preferences (discard changes)
    if (preferences) {
      setFormData({
        currency: preferences.currency,
        currencyDisplay: preferences.currencyDisplay,
        dateFormat: preferences.dateFormat,
        timeFormat: preferences.timeFormat,
        financialYearStart: preferences.financialYearStart,
        theme: preferences.theme,
        dashboardPreferences: { ...preferences.dashboardPreferences },
        transactionRetentionYears: preferences.transactionRetentionYears,
        payFrequency: preferences.payFrequency,
        payDay: preferences.payDay,
        payAnchorDate: preferences.payAnchorDate,
        estimatedNetPay: preferences.estimatedNetPay,
        payTransactionName: preferences.payTransactionName,
        gstRegistered: preferences.gstRegistered,
        businessName: preferences.businessName,
        businessAbn: preferences.businessAbn,
      });
      setIsDirty(false);
      setHasUnsavedChanges(false);
    }
    // Proceed with navigation
    if (pendingNavigation) {
      isNavigatingRef.current = true;
      navigate(pendingNavigation);
      setPendingNavigation(null);
    }
  }, [pendingNavigation, navigate, preferences]);

  const handleNavigationDialogCancel = useCallback(() => {
    setShowNavigationDialog(false);
    setPendingNavigation(null);
  }, []);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (fetchError || !preferences) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Account Settings"
          description="Language, currency, date format preferences"
          helpSlug="settings-account"
          headingLevel="h2"
        />
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <div className="flex items-center gap-2 text-red-800 dark:text-red-200">
            <AlertCircle className="h-5 w-5" />
            <p>{fetchError?.message || 'Failed to load preferences'}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
        {/* Page Header */}
        <PageHeader
          title="Account Settings"
          description="Language, currency, date format preferences"
          helpSlug="settings-account"
          headingLevel="h2"
        />

      {/* Error Message */}
      {errorMessage && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <div className="flex items-center gap-2 text-red-800 dark:text-red-200">
            <AlertCircle className="h-5 w-5" />
            <p>{errorMessage}</p>
          </div>
        </div>
      )}

      {/* Success Message */}
      {saveStatus === 'saved' && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
          <div className="flex items-center gap-2 text-green-800 dark:text-green-200">
            <CheckCircle className="h-5 w-5" />
            <p>Preferences saved successfully</p>
          </div>
        </div>
      )}

      {/* General Settings Section */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-2">
            <Settings className="h-5 w-5 text-gray-500 dark:text-gray-400" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              General Settings
            </h3>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Customize your financial management experience
          </p>
        </div>

        <div className="space-y-6">
          {/* Currency Settings */}
          <div className="border-b border-gray-200 dark:border-gray-700 pb-6">
            <div className="flex items-center gap-2 mb-4">
              <DollarSign className="h-5 w-5 text-gray-500 dark:text-gray-400" />
              <h4 className="text-base font-medium text-gray-900 dark:text-gray-100">Currency Settings</h4>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="currency" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Primary Currency
                </label>
                <div className="relative">
                  <select
                    id="currency"
                    value={formData.currency || preferences.currency}
                    onChange={(e) => handleFieldChange('currency')(e.target.value as any)}
                    className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 appearance-none cursor-pointer"
                  >
                    <option value="USD">$ US Dollar (USD)</option>
                    <option value="EUR">€ Euro (EUR)</option>
                    <option value="GBP">£ British Pound (GBP)</option>
                    <option value="JPY">¥ Japanese Yen (JPY)</option>
                    <option value="CAD">$ Canadian Dollar (CAD)</option>
                    <option value="AUD">$ Australian Dollar (AUD)</option>
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" aria-hidden="true" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Currency Display
                </label>
                <div className="flex items-center gap-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="currencyDisplay"
                      value="symbol"
                      checked={(formData.currencyDisplay || preferences.currencyDisplay) === 'symbol'}
                      onChange={(e) => handleFieldChange('currencyDisplay')(e.target.value as any)}
                      className="mr-2"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">Symbol ($1,234.56)</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="currencyDisplay"
                      value="code"
                      checked={(formData.currencyDisplay || preferences.currencyDisplay) === 'code'}
                      onChange={(e) => handleFieldChange('currencyDisplay')(e.target.value as any)}
                      className="mr-2"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">Code (AUD 1,234.56)</span>
                  </label>
                </div>
              </div>
            </div>
          </div>

          {/* Date & Time Settings */}
          <div className="border-b border-gray-200 dark:border-gray-700 pb-6">
            <div className="flex items-center gap-2 mb-4">
              <Calendar className="h-5 w-5 text-gray-500 dark:text-gray-400" />
              <h4 className="text-base font-medium text-gray-900 dark:text-gray-100">Date & Time Settings</h4>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="dateFormat" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Date Format
                </label>
                <div className="relative">
                  <select
                    id="dateFormat"
                    value={formData.dateFormat || preferences.dateFormat}
                    onChange={(e) => handleFieldChange('dateFormat')(e.target.value as any)}
                    className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 appearance-none cursor-pointer"
                  >
                    <option value="MM/DD/YYYY">MM/DD/YYYY (Example: 12/25/2024)</option>
                    <option value="DD/MM/YYYY">DD/MM/YYYY (Example: 25/12/2024)</option>
                    <option value="YYYY-MM-DD">YYYY-MM-DD (Example: 2024-12-25)</option>
                    <option value="DD-MM-YYYY">DD-MM-YYYY (Example: 25-12-2024)</option>
                    <option value="MM-DD-YYYY">MM-DD-YYYY (Example: 12-25-2024)</option>
                    <option value="DDD MMM DD YYYY">DDD MMM DD YYYY (Example: Fri Oct 31 2025)</option>
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" aria-hidden="true" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Time Format
                </label>
                <div className="flex items-center gap-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="timeFormat"
                      value="12h"
                      checked={(formData.timeFormat || preferences.timeFormat) === '12h'}
                      onChange={(e) => handleFieldChange('timeFormat')(e.target.value as any)}
                      className="mr-2"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">12h (AM/PM)</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="timeFormat"
                      value="24h"
                      checked={(formData.timeFormat || preferences.timeFormat) === '24h'}
                      onChange={(e) => handleFieldChange('timeFormat')(e.target.value as any)}
                      className="mr-2"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">24h</span>
                  </label>
                </div>
              </div>
              <div>
                <label htmlFor="financialYearStart" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Financial Year Start
                </label>
                <div className="relative">
                  <select
                    id="financialYearStart"
                    value={formData.financialYearStart || preferences.financialYearStart}
                    onChange={(e) => handleFieldChange('financialYearStart')(e.target.value as any)}
                    className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 appearance-none cursor-pointer"
                  >
                    <option value="January">January 1st - December 31st</option>
                    <option value="April">April 1st - March 31st</option>
                    <option value="July">July 1st - June 30th</option>
                    <option value="October">October 1st - September 30th</option>
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" aria-hidden="true" />
                </div>
              </div>
              <div>
                <label htmlFor="language" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Language
                </label>
                <div className="relative">
                  <select
                    id="language"
                    disabled
                    className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed appearance-none"
                  >
                    <option>English (Coming Soon)</option>
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" aria-hidden="true" />
                </div>
              </div>
              <div>
                <label htmlFor="timezone" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Time Zone
                </label>
                <div className="relative">
                  <select
                    id="timezone"
                    disabled
                    className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed appearance-none"
                  >
                    <option>Australia/Sydney (Coming Soon)</option>
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" aria-hidden="true" />
                </div>
              </div>
            </div>
          </div>

          {/* Theme & Appearance */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <Palette className="h-5 w-5 text-gray-500 dark:text-gray-400" />
              <h4 className="text-base font-medium text-gray-900 dark:text-gray-100">Theme & Appearance</h4>
            </div>
            <div className="flex items-center gap-4">
              <button
                type="button"
                onClick={() => handleFieldChange('theme')('light')}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 transition-colors ${
                  (formData.theme || preferences.theme) === 'light'
                    ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
                    : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:border-gray-400'
                }`}
              >
                <Sun className="h-5 w-5" />
                <span className="text-sm font-medium">Light Mode</span>
              </button>
              <button
                type="button"
                onClick={() => handleFieldChange('theme')('dark')}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 transition-colors ${
                  (formData.theme || preferences.theme) === 'dark'
                    ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
                    : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:border-gray-400'
                }`}
              >
                <Moon className="h-5 w-5" />
                <span className="text-sm font-medium">Dark Mode</span>
              </button>
              <button
                type="button"
                onClick={() => handleFieldChange('theme')('auto')}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 transition-colors ${
                  (formData.theme || preferences.theme) === 'auto'
                    ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
                    : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:border-gray-400'
                }`}
              >
                <Monitor className="h-5 w-5" />
                <span className="text-sm font-medium">Auto (System)</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Pay Day Section */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-2">
            <Wallet className="h-5 w-5 text-gray-500 dark:text-gray-400" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Pay Day
            </h3>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Used to plan bills against your pay cycle.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="payFrequency" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Pay frequency
            </label>
            <div className="relative">
              <select
                id="payFrequency"
                value={formData.payFrequency ?? ''}
                onChange={(e) => handlePayFrequencyChange(e.target.value)}
                className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 appearance-none cursor-pointer"
              >
                <option value="">Not set</option>
                <option value="weekly">Weekly</option>
                <option value="fortnightly">Fortnightly</option>
                <option value="monthly">Monthly</option>
              </select>
              <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" aria-hidden="true" />
            </div>
          </div>

          {formData.payFrequency && (
            <div>
              <label htmlFor="payDay" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Pay day
              </label>
              {formData.payFrequency === 'monthly' ? (
                <input
                  id="payDay"
                  type="number"
                  min={1}
                  max={31}
                  value={formData.payDay ?? ''}
                  onChange={(e) => {
                    const raw = e.target.value;
                    handleFieldChange('payDay')(raw === '' ? null : parseInt(raw, 10));
                  }}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              ) : (
                <div className="relative">
                  <select
                    id="payDay"
                    value={formData.payDay ?? ''}
                    onChange={(e) => {
                      const raw = e.target.value;
                      handleFieldChange('payDay')(raw === '' ? null : parseInt(raw, 10));
                    }}
                    className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 appearance-none cursor-pointer"
                  >
                    <option value="">Select weekday</option>
                    {WEEKDAY_OPTIONS.map((day) => (
                      <option key={day.value} value={day.value}>{day.label}</option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" aria-hidden="true" />
                </div>
              )}
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                If your pay day falls on a weekend or public holiday, use your usual scheduled day.
              </p>
            </div>
          )}

          {formData.payFrequency === 'fortnightly' && (
            <div>
              <label htmlFor="payAnchorDate" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Anchor date
              </label>
              <input
                id="payAnchorDate"
                type="date"
                value={formData.payAnchorDate ?? ''}
                onChange={(e) => handleFieldChange('payAnchorDate')(e.target.value || null)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                Date of a recent payday — used to align fortnightly cycles.
              </p>
            </div>
          )}

          <div>
            <label htmlFor="estimatedNetPay" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Estimated take-home amount ({formData.currency || preferences.currency})
            </label>
            <input
              id="estimatedNetPay"
              type="number"
              min="0.01"
              step="0.01"
              placeholder="2,500.00"
              value={formData.estimatedNetPay ?? ''}
              onChange={(e) => {
                const raw = e.target.value;
                handleFieldChange('estimatedNetPay')(raw === '' ? null : parseFloat(raw));
              }}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          <div className="md:col-span-2">
            <label htmlFor="payTransactionName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Pay transaction name
            </label>
            <input
              id="payTransactionName"
              type="text"
              placeholder="e.g. ACME PAYROLL"
              value={formData.payTransactionName ?? ''}
              onChange={(e) => {
                const raw = e.target.value;
                handleFieldChange('payTransactionName')(raw.trim() === '' ? null : raw);
              }}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
              Used to find matching income transactions for pay amount lookups (description contains this text).
            </p>
          </div>
        </div>
      </div>

      {/* Dashboard Customization Section */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-2">
            <LayoutDashboard className="h-5 w-5 text-gray-500 dark:text-gray-400" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Dashboard Customization
            </h3>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Control what appears on your dashboard
          </p>
        </div>

        <div className="space-y-4">
          {/* Show Spending Chart Toggle */}
          <div className="flex items-center justify-between py-3 border-b border-gray-200 dark:border-gray-700">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <BarChart3 className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                <label htmlFor="showSpendingChart" className="font-medium text-gray-900 dark:text-gray-100">
                  Show Spending Chart
                </label>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                Display spending trends on dashboard
              </p>
            </div>
            <button
              type="button"
              id="showSpendingChart"
              onClick={() => handleDashboardToggle('showSpendingChart')}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                formData.dashboardPreferences?.showSpendingChart ?? preferences.dashboardPreferences.showSpendingChart
                  ? 'bg-primary-600'
                  : 'bg-gray-300 dark:bg-gray-600'
              }`}
              role="switch"
              aria-checked={formData.dashboardPreferences?.showSpendingChart ?? preferences.dashboardPreferences.showSpendingChart}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  formData.dashboardPreferences?.showSpendingChart ?? preferences.dashboardPreferences.showSpendingChart
                    ? 'translate-x-6'
                    : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* Show Recent Transactions Toggle */}
          <div className="flex items-center justify-between py-3 border-b border-gray-200 dark:border-gray-700">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <List className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                <label htmlFor="showRecentTransactions" className="font-medium text-gray-900 dark:text-gray-100">
                  Show Recent Transactions
                </label>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                Display latest transactions on dashboard
              </p>
            </div>
            <button
              type="button"
              id="showRecentTransactions"
              onClick={() => handleDashboardToggle('showRecentTransactions')}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                formData.dashboardPreferences?.showRecentTransactions ?? preferences.dashboardPreferences.showRecentTransactions
                  ? 'bg-primary-600'
                  : 'bg-gray-300 dark:bg-gray-600'
              }`}
              role="switch"
              aria-checked={formData.dashboardPreferences?.showRecentTransactions ?? preferences.dashboardPreferences.showRecentTransactions}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  formData.dashboardPreferences?.showRecentTransactions ?? preferences.dashboardPreferences.showRecentTransactions
                    ? 'translate-x-6'
                    : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* Show Budget Overview Toggle */}
          <div className="flex items-center justify-between py-3">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <TrendingUp className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                <label htmlFor="showBudgetOverview" className="font-medium text-gray-900 dark:text-gray-100">
                  Show Spending Trends by Category
                </label>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                Daily, weekly & monthly patterns
              </p>
            </div>
            <button
              type="button"
              id="showBudgetOverview"
              onClick={() => handleDashboardToggle('showBudgetOverview')}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                formData.dashboardPreferences?.showBudgetOverview ?? preferences.dashboardPreferences.showBudgetOverview
                  ? 'bg-primary-600'
                  : 'bg-gray-300 dark:bg-gray-600'
              }`}
              role="switch"
              aria-checked={formData.dashboardPreferences?.showBudgetOverview ?? preferences.dashboardPreferences.showBudgetOverview}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  formData.dashboardPreferences?.showBudgetOverview ?? preferences.dashboardPreferences.showBudgetOverview
                    ? 'translate-x-6'
                    : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        </div>
      </div>

      <BusinessReportingSettingsSection
        formData={formData}
        onChange={(patch) => setFormData((prev) => ({ ...prev, ...patch }))}
      />

      {/* Data Retention Section */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-2">
            <Archive className="h-5 w-5 text-gray-500 dark:text-gray-400" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Data Retention
            </h3>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Configure how long to keep your transaction history
          </p>
        </div>

        <div>
          <label htmlFor="transactionRetentionYears" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Transaction History Retention
          </label>
          <div className="relative w-1/2">
            <select
              id="transactionRetentionYears"
              value={formData.transactionRetentionYears ?? preferences.transactionRetentionYears ?? ''}
              onChange={(e) => {
                const value = e.target.value === '' ? null : parseInt(e.target.value, 10);
                handleFieldChange('transactionRetentionYears')(value);
              }}
              className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 appearance-none cursor-pointer"
            >
              <option value="1">1 Year</option>
              <option value="2">2 Years</option>
              <option value="5">5 Years</option>
              <option value="10">10 Years</option>
              <option value="">Keep Forever</option>
            </select>
            <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" aria-hidden="true" />
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
            Older transactions will be automatically archived but remain accessible for reporting.
          </p>
        </div>
      </div>

      {/* Validation Errors */}
      {Object.keys(validationErrors).length > 0 && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
          <div className="flex items-start gap-2">
            <AlertCircle className="h-5 w-5 text-yellow-800 dark:text-yellow-200 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200 mb-2">
                Please fix the following errors:
              </p>
              <ul className="list-disc list-inside text-sm text-yellow-700 dark:text-yellow-300 space-y-1">
                {Object.entries(validationErrors).map(([field, message]) => (
                  <li key={field}>{message}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Save Changes Button */}
      <div className="flex justify-end pt-6">
        <button
          type="button"
          onClick={handleSave}
          disabled={!isDirty || saveStatus === 'saving'}
          data-loading={saveStatus === 'saving' ? 'true' : undefined}
          className="btn-primary btn-md flex items-center gap-2"
        >
          {saveStatus === 'saving' ? (
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          ) : saveStatus === 'saved' ? (
            <CheckCircle className="h-4 w-4" aria-hidden="true" />
          ) : (
            <Save className="h-4 w-4" aria-hidden="true" />
          )}
          {saveStatus === 'saving' ? 'Saving...' : saveStatus === 'saved' ? 'Saved' : 'Save Changes'}
        </button>
      </div>

      {/* Navigation Guard Dialog */}
      {showNavigationDialog && (
        <div
          className="fixed inset-0 z-50 overflow-y-auto"
          role="dialog"
          aria-modal="true"
          aria-labelledby="navigation-dialog-title"
        >
          {/* Backdrop */}
          <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" />
          
          {/* Dialog */}
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full p-6">
              <h3 id="navigation-dialog-title" className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                Unsaved Changes
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                You have unsaved changes. What would you like to do?
              </p>
              
              <div className="flex flex-col sm:flex-row gap-3 justify-end">
                <button
                  type="button"
                  onClick={handleNavigationDialogCancel}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleNavigationDialogDiscard}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                >
                  Discard Changes
                </button>
                <button
                  type="button"
                  onClick={handleNavigationDialogSave}
                  disabled={saveStatus === 'saving'}
                  className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {saveStatus === 'saving' ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span>Saving...</span>
                    </>
                  ) : (
                    'Save Changes'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

